import type { WinBucket } from "./resolveClosedWin";

// CTA-zone arbitration: one optional full-width primary + a required pair of
// secondary tiles, resolved from three signals. Variant matrix:
//
//   | Intent     | Linked  | Closed | Primary    | Secondary            |
//   | bill       | false   | false  | billHelp   | monitoring, coverage |
//   | insurance  | false   | false  | monitoring | billHelp, coverage   |
//   | –          | true    | false  | (none)     | billHelp, coverage   |
//   | –          | unknown | false  | (none)     | billHelp, coverage   |
//   | –          |  any    | true   | closedCase | billHelp, monitoring |

// The three get-help CTAs that can occupy a secondary tile.
export type SecondaryCtaType = "billHelp" | "billMonitoring" | "coverage";

export type CtaType = SecondaryCtaType | "closedCase";

export type CtaIntent = "bill" | "insurance";

export type CtaPrimary =
  | { type: "billHelp" }
  | { type: "billMonitoring" }
  | {
      type: "closedCase";
      issueId: string;
      bucket: WinBucket;
      caseType?: string | null;
      providerName?: string;
      savings?: number;
    };

export type CtaLayout = {
  /** Optional full-width hero. Absent for a linked user with nothing new. */
  primary?: CtaPrimary;
  /** The always-present pair of secondary tiles, left → right. */
  secondary: [SecondaryCtaType, SecondaryCtaType];
};

export type CtaLayoutInput = {
  intent: CtaIntent;
  /** undefined = link status unknown (query failed); suppresses both link-dependent heroes. */
  insuranceLinked: boolean | undefined;
  /** The most-recent unacknowledged positive closed-case win, if any. */
  closedWin?: {
    issueId: string;
    bucket: WinBucket;
    caseType?: string | null;
    providerName?: string;
    savings?: number;
  };
};

export function resolveCtaLayout(input: CtaLayoutInput): CtaLayout {
  const { intent, insuranceLinked, closedWin } = input;

  // Closed-case win takes the primary regardless of the other signals; the
  // secondary pair shifts to bill help + monitoring.
  if (closedWin) {
    return {
      primary: { type: "closedCase", ...closedWin },
      secondary: ["billHelp", "billMonitoring"],
    };
  }

  // Unlinked: lead with the intent hero, offer the other two as secondary.
  // Unknown status falls through — pitching a link to an already-linked user
  // is worse than showing no hero.
  if (insuranceLinked === false) {
    return intent === "insurance"
      ? {
          primary: { type: "billMonitoring" },
          secondary: ["billHelp", "coverage"],
        }
      : {
          primary: { type: "billHelp" },
          secondary: ["billMonitoring", "coverage"],
        };
  }

  // Linked, no closed win: no primary, just the evergreen pair.
  return { secondary: ["billHelp", "coverage"] };
}
